<template>
  <div v-if="totalPages > 1" class="pagination">
    <button
      class="pagination__item pagination__arrow"
      :disabled="currentPage === 1"
      @click="changePage(currentPage - 1)"
    >
      <svg
        class="arrow-icon-left"
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.4"
          d="M6.33203 1L1.33203 6L6.33203 11"
          stroke="#205640"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <button
      v-for="page in visiblePages"
      :key="page"
      class="pagination__item"
      :class="{ 'is-active': page === currentPage }"
      @click="changePage(page)"
    >
      {{ page }}
    </button>

    <button
      class="pagination__item pagination__arrow"
      :disabled="currentPage === totalPages"
      @click="changePage(currentPage + 1)"
    >
      <svg
        class="arrow-icon-right"
        width="8"
        height="12"
        viewBox="0 0 8 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.4"
          d="M6.33203 1L1.33203 6L6.33203 11"
          stroke="#205640"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalItems: {
      type: Number,
      required: true,
      default: 0,
    },
    itemsPerPage: {
      type: Number,
      required: true,
      default: 10,
    },
    maxVisiblePages: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      isMobile: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    maxPages() {
      return this.isMobile ? 4 : 6;
    },
    visiblePages() {
      const maxPages = this.maxPages;

      if (this.totalPages <= maxPages) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      const pages = [];
      const half = Math.floor(maxPages / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxPages - 1);

      if (end - start + 1 < maxPages) {
        start = Math.max(1, end - maxPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit("page-change", page);
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  align-items: center;
  margin: 25px 0;
}

.pagination__item {
  padding: 0;
  border: 1px solid transparent;
  border-radius: 1000px;
  background: #fff;
  color: #205640;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 45px;
  height: 45px;
  min-width: 45px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  background-image: linear-gradient(white, white),
    linear-gradient(105.18deg, #d3d0ff 0%, #205640 50%, #ffffff 100%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
}

.pagination__item:hover:not(:disabled) {
  border-color: #205640;
  background: #205640;
  color: #fff;
}

.pagination__item.is-active {
  border-color: #205640;
  background: #205640;
  color: #fff;
}

.pagination__item:disabled {
  opacity: 0.5;
}

.pagination__arrow {
  font-weight: bold;
  font-size: 16px;
}

.pagination__item:hover:not(:disabled) > svg > path {
  stroke: #fff;
}

.arrow-icon-right {
  transform: rotate(180deg);
}

@media (max-width: 768px) {
  .pagination {
    gap: 6px;
    margin-bottom: 40px;
  }

  .pagination__item {
    width: 40px;
    height: 40px;
    min-width: 40px;
    font-size: 14px;
  }

  .pagination__item:hover:not(:disabled) {
    border-color: #205640;
    background: #fff;
    color: #205640;
  }

  .pagination__item.is-active:hover:not(:disabled) {
    border-color: #205640;
    background: #205640;
    color: #fff;
  }

  .pagination__item:hover:not(:disabled) > svg > path {
    stroke: #205640;
  }
}

@media (max-width: 480px) {
  .pagination {
    gap: 4px;
  }

  .pagination__item {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 13px;
  }
}
</style>
